/**
 * Where the student is standing, surviving a refresh.
 *
 * SessionStore preserves the student's WORK - journal, wafer, panel state. This
 * preserves their PRESENCE: that they already entered the cleanroom, where they
 * stood, which way they faced, and which tool they had open. Without it every
 * F5 marched them back through the gowning airlock and dropped them at the
 * door, which turns an accidental refresh into a two-minute walk and makes the
 * entry screen - meant as a one-time ritual - into a recurring toll.
 *
 * ONE PLACE PER FAB. There are two halls now (fab/hall.ts) and a student who
 * walks to bay 07 in Fab A, switches to Fab B to see the same tools without
 * the wall, walks to bay 12 there and switches back expects to be at bay 07
 * again - each fab remembers where you were in IT. So the key carries the
 * layout, and a switch (`carryPlaceTo`) seeds the other fab with where you
 * are standing the FIRST time only, since the two share a floor plan and
 * landing at the airlock to look at a tool you were just in front of would
 * be the wrong kind of memory.
 *
 * Kept separate from SessionStore on purpose: that file owns the record a
 * student is assessed on, this owns a camera pose. Different keys, different
 * risk if lost - a lost place costs a walk, a lost journal costs a debrief.
 *
 * Same storage discipline as its sibling: localStorage behind try/catch
 * (private windows and full disks throw), and an expiry so a shared lab
 * machine does not spawn next week's student mid-aisle at a tool they have
 * never seen. A fresh visitor still gets the airlock, which is also the
 * pointer-lock consent and the keys legend - returning students have read it.
 */

import { HALL_LAYOUT, planIdOf, type HallLayout } from './hall'
import { FAB, TOOLS, toolFaceZ, toolSize } from './layout'

const keyFor = (layout: HallLayout): string => `simfab.place.v1.${layout}`
/** The fab the hall is built with right now - read at each call, since it switches in place. */
const key = (): string => keyFor(HALL_LAYOUT)
/**
 * The key from before there were two fabs. Read once, as Fab A's place -
 * the only hall that existed when it was written - then retired, so nobody
 * loses their spot on the day the second fab appears.
 */
const LEGACY_KEY = 'simfab.place.v1'
const MAX_AGE_DAYS = 14

export interface StoredPlace {
  savedAt: number
  x: number
  y: number
  z: number
  /** Body yaw [rad]. */
  yaw: number
  /** Camera pitch [rad]. */
  pitch: number
  /** Tool panel that was open, if any. */
  tool: string | null
}

/** One key's place, if it is there, well-formed, and not stale. */
function read(key: string): StoredPlace | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const place = JSON.parse(raw) as StoredPlace
    if (typeof place.x !== 'number' || typeof place.yaw !== 'number') return null
    if (Date.now() - place.savedAt > MAX_AGE_DAYS * 86_400_000) {
      localStorage.removeItem(key)
      return null
    }
    return place
  } catch {
    return null
  }
}

function write(key: string, place: StoredPlace): void {
  try {
    localStorage.setItem(key, JSON.stringify(place))
  } catch {
    // A place that cannot be saved is a walk, not a failure.
  }
}

/**
 * A pose that is not in this hall any more.
 *
 * A fab somebody built is re-laid every time they change it (hall.ts
 * `refreshCustomHall`), and deleting four tools shortens the hall. The place
 * saved before that edit can land outside the end wall or inside the row -
 * so it is checked against the hall as it now stands, and a pose that no
 * longer fits is forgotten rather than walked into. Fab A and Fab B never
 * change shape, so this only ever fires on a floor its author edited.
 */
const stillInTheHall = (p: StoredPlace): boolean => Math.abs(p.x) < FAB.length / 2 - 1.5 && !insideATool(p)

/**
 * Whether a pose is inside a station's footprint, plus a shoulder's width.
 * A re-laid row (hall.ts `refreshCustomHall`) can stand a mainframe where
 * the author was standing, and a pose restored there is a pose inside the
 * colliders. A bay tool occupies its face line outward; an axis tool its
 * own box.
 */
function insideATool(p: StoredPlace): boolean {
  const margin = 0.6
  return TOOLS.some((t) => {
    const size = toolSize(t)
    if (Math.abs(p.x - t.position[0]) > size.w / 2 + margin) return false
    if (t.position[2] === 0) return Math.abs(p.z) <= size.d / 2 + margin
    return Math.sign(p.z) === Math.sign(t.position[2]) && Math.abs(p.z) >= Math.abs(toolFaceZ(t)) - margin
  })
}

export function loadPlace(): StoredPlace | null {
  const here = read(key())
  if (here) return stillInTheHall(here) ? here : null
  // Migration, once: Fab A inherits the pre-split key and it goes away.
  if (HALL_LAYOUT !== 'bayChase') return null
  const old = read(LEGACY_KEY)
  if (!old) return null
  write(key(), old)
  try {
    localStorage.removeItem(LEGACY_KEY)
  } catch {
    // Left behind, it is read again next time and re-migrated. Harmless.
  }
  return old
}

export function savePlace(place: Omit<StoredPlace, 'savedAt'>): void {
  write(key(), { ...place, savedAt: Date.now() })
}

/**
 * Where they stood in a fab they have just LEFT, under that fab's key.
 *
 * The in-place switch flips the layout before the composition root hears of
 * it, so by then `savePlace` would file the pose under the destination. This
 * names the fab explicitly.
 */
export function savePlaceAs(layout: HallLayout, place: Omit<StoredPlace, 'savedAt'>): void {
  write(keyFor(layout), { ...place, savedAt: Date.now() })
}

/**
 * Forget where the student stood. "Back to entrance" calls it on the way
 * out to the airlock, so a refresh from there is a first visit again. Nothing
 * here has to guard against the next save: main.tsx only writes the place
 * while the student is entered, and at the airlock they are not.
 */
export function clearPlace(): void {
  try {
    localStorage.removeItem(key())
  } catch {
    // Nothing to forget.
  }
}

/**
 * Give the other fab somewhere to stand, the first time.
 *
 * Called by the menu just before it stores the choice and reloads. Only when
 * the destination has NO place of its own: after that each fab keeps its own
 * memory, which is the whole point. The pose copied is this fab's last saved
 * one - at most two seconds behind the student's feet, and the reload's own
 * `beforeunload` write lands under THIS fab's key, not the destination's.
 */
export function carryPlaceTo(layout: HallLayout): void {
  if (layout === HALL_LAYOUT) return
  // ONLY BETWEEN THE TWO AUTHORED FABS. They share a floor plan, so where
  // you stood in one is a place in the other. A fab somebody built is a
  // different building - a different length, a different row, quite
  // possibly no tool at all where you were standing - and carrying a pose
  // into it would drop the student in a wall or past the end of the hall.
  // They walk in through the airlock, which is the right introduction to a
  // floor they have not seen.
  if (planIdOf(layout) || planIdOf(HALL_LAYOUT)) return
  const there = read(keyFor(layout))
  if (there) return
  const here = read(key())
  if (here) write(keyFor(layout), here)
}
